/**
 * Shared badge component for HOD / Deputy HOD / Portal Group roles.
 *
 * Derives role from the employee object's is_hod / is_deputy_hod flags
 * and portal_groups array (all returned by StaffUserSerializer).
 *
 * Props:
 *   employee    – object with { is_hod, is_deputy_hod, hod_departments,
 *                 deputy_hod_departments, portal_groups }
 *   size             – "sm" (table/list rows) | "md" (default, modals/headers)
 *   showPortalGroups – render portal-group badges (default true)
 *   className        – additional wrapper classes
 */
import React from 'react';

const SIZES = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-0.5 text-xs',
};

const PORTAL_GROUP_STYLES = {
  HR: 'bg-red-100 text-red-800 border-red-200',
  Manager: 'bg-blue-100 text-blue-800 border-blue-200',
  CourseNominator: 'bg-teal-100 text-teal-800 border-teal-200',
};

const DEFAULT_GROUP_STYLE = 'bg-gray-100 text-gray-800 border-gray-200';

const RoleBadge = ({ employee, size = 'md', className = '', showPortalGroups = true }) => {
  if (!employee) return null;

  const {
    is_hod, is_deputy_hod, hod_departments, deputy_hod_departments,
    portal_groups,
  } = employee;

  const hasOrgRoles = is_hod || is_deputy_hod;
  const hasPortalGroups = portal_groups?.length > 0;

  if (!hasOrgRoles && (!hasPortalGroups || !showPortalGroups)) return null;

  const sizeClass = SIZES[size] || SIZES.md;
  const badges = [];

  if (is_hod) {
    const depts = hod_departments?.length ? hod_departments.join(', ') : null;
    badges.push(
      <span
        key="hod"
        title={depts ? `Head of Department — ${depts}` : 'Head of Department'}
        className={`inline-flex items-center font-semibold rounded-full border bg-purple-100 text-purple-800 border-purple-200 ${sizeClass}`}
      >
        HOD
      </span>
    );
  }

  if (is_deputy_hod) {
    const depts = deputy_hod_departments?.length ? deputy_hod_departments.join(', ') : null;
    badges.push(
      <span
        key="dy-hod"
        title={depts ? `Deputy Head of Department — ${depts}` : 'Deputy Head of Department'}
        className={`inline-flex items-center font-semibold rounded-full border bg-violet-100 text-violet-700 border-violet-200 ${sizeClass}`}
      >
        Dy HOD
      </span>
    );
  }

  if (showPortalGroups && hasPortalGroups) {
    for (const groupName of portal_groups) {
      const style = PORTAL_GROUP_STYLES[groupName] || DEFAULT_GROUP_STYLE;
      badges.push(
        <span
          key={`pg-${groupName}`}
          title={`Portal group: ${groupName}`}
          className={`inline-flex items-center font-semibold rounded-full border ${style} ${sizeClass}`}
        >
          {groupName}
        </span>
      );
    }
  }

  return <>{badges.map(b => b)}</>;
};

export default RoleBadge;
